import { createAsyncThunk } from '@reduxjs/toolkit';
import jwtService from 'app/services/jwtService';
import { RootState } from 'app/store';
import { showMessage } from 'app/store/fuse/messageSlice';
import i18n from 'i18n';
import {
  createWebPushSubscriptionApi,
  deleteWebPushSubscriptionApi,
  getPublicVapidKeyApi,
  searchWebPushSubscriptionApi,
  updateWebPushSubscriptionInfoApi,
  updateWebPushSubscriptionSelectedStoresApi,
} from '../api';
import { WebPushNotificationTypeEnum } from '../type';
import { getBrowserName, getSubscriptionInfo } from '../utils/device.util';

export const subscribeToWebPushNotificationsThunk = createAsyncThunk(
  'webPush/subscribeToWebPushNotifications',
  async (_, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const userId = state.auth.user.data.id;
      const storeId = state.storesApp.stores.ids[0]; // Assuming the first store is selected
      const vapidPublicKey = await getPublicVapidKeyApi();
      if (navigator.serviceWorker.controller) {
        const browser = getBrowserName();
        const { deviceId, p256dh, auth, endpoint, expirationTime } = await getSubscriptionInfo(vapidPublicKey);

        const subscription = await createWebPushSubscriptionApi({
          deviceId,
          userId,
          browser,
          selectedStores: [
            {
              storeId,
              types: [WebPushNotificationTypeEnum.RECEIPT],
              tagIds: [],
            },
          ],
          endpoint,
          expirationTime,
          keys: {
            p256dh: p256dh.toString(),
            auth: auth.toString(),
          },
        });
        dispatch(
          showMessage({
            message: i18n.t('notification.successes.subscription', { ns: 'notification' }),
            variant: 'success',
          }),
        );
        return subscription;
      }
      throw new Error('Service Worker is not ready');
    } catch (error) {
      dispatch(
        showMessage({ message: i18n.t('notification.errors.subscription', { ns: 'notification' }), variant: 'error' }),
      );
      return rejectWithValue(error);
    }
  },
);

export const unsubscribeFromWebPushNotificationsThunk = createAsyncThunk(
  'webPush/unsubscribeFromWebPushNotifications',
  async (subscriptionId: string, { dispatch, rejectWithValue }) => {
    try {
      await deleteWebPushSubscriptionApi(subscriptionId);
      dispatch(
        showMessage({
          message: i18n.t('notification.successes.unsubscription', { ns: 'notification' }),
          variant: 'success',
        }),
      );
    } catch (error: any) {
      dispatch(
        showMessage({
          message: i18n.t('notification.errors.unsubscription', { ns: 'notification' }),
          variant: 'error',
        }),
      );
      return rejectWithValue(error);
    }
  },
);

export const getWebPushSubscriptionThunk = createAsyncThunk(
  'webPush/getWebPushSubscription',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const vapidPublicKey = await getPublicVapidKeyApi();
      const { deviceId, endpoint, expirationTime, p256dh, auth } = await getSubscriptionInfo(vapidPublicKey);
      const userId = jwtService.getUserId();
      if (!userId) {
        throw new Error('User is not authenticated');
      }
      const subscriptionInfo = {
        deviceId,
        endpoint,
        expirationTime,
        keys: {
          p256dh: p256dh.toString(),
          auth: auth.toString(),
        },
      };
      const subscription = await searchWebPushSubscriptionApi(userId, deviceId);
      await updateWebPushSubscriptionInfoApi(subscription.id, subscriptionInfo);
      return subscription;
    } catch (error: any) {
      if (error.response && error.response.status === 404) {
        return null;
      }
      dispatch(showMessage({ message: i18n.t('notification.errors.fetch', { ns: 'notification' }), variant: 'error' }));
      return rejectWithValue(error);
    }
  },
);

export const updateWebPushSubscriptionStoresThunk = createAsyncThunk(
  'webPush/updateWebPushSubscriptionStores',
  async (_, { dispatch, getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const subscription = state.webPush.subscription.data;
      if (!subscription) {
        throw new Error('No subscription found');
      }
      const response = await updateWebPushSubscriptionSelectedStoresApi(subscription.id, subscription.selectedStores);
      dispatch(
        showMessage({
          message: i18n.t('notification.successes.update', { ns: 'notification' }),
          variant: 'success',
        }),
      );
      return response;
    } catch (error) {
      dispatch(
        showMessage({
          message: i18n.t('notification.errors.update', { ns: 'notification' }),
          variant: 'error',
        }),
      );
      return rejectWithValue(error);
    }
  },
);

export const removeWebPushSubscriptionInfoThunk = createAsyncThunk(
  'webPush/removeWebPushSubscriptionInfo',
  async (subscriptionId: string, { dispatch, rejectWithValue }) => {
    try {
      const subscription = {
        deviceId: '',
        endpoint: '',
        expirationTime: null,
        keys: {
          p256dh: '',
          auth: '',
        },
      };
      await updateWebPushSubscriptionInfoApi(subscriptionId, subscription);
      return subscription;
    } catch (error) {
      dispatch(
        showMessage({
          message: i18n.t('notification.errors.update', { ns: 'notification' }),
          variant: 'error',
        }),
      );
      return rejectWithValue(error);
    }
  },
);
